/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DataPoint, DataSeries, Primitive } from 'c3';
import * as React from 'react';
import { renderToString } from 'react-dom/server';
import { defineMessages } from 'react-intl';
import { BasicChart } from 'src/core/components/BasicChart';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { ChartDataProps } from 'src/core/types/chart';
import { transformStackedBarChartData } from 'src/core/utils/chartDataTransforms';

require('./stackedBarChartStyles.less');

export type StackedBarChartProps<X extends Primitive> = ChartDataProps<X, number[]> & {
    /**
     * The colors of the groups in the bar.
     */
    colors?: string[];

    /**
     * The groups names.
     */
    groups?: string[];

    /**
     * The bar width.
     */
    barWidth?: number;

    /**
     * The tooltip component to be rendered
     */
    onRenderTooltip: (index: number) => JSX.Element;
};

const MIN_WITHOUT_PADDING: number = 17;
const MAX_BARS: number = 500;

const messages = defineMessages({
    stackedBarChartEmptyLabel: {
        id: 'core.charts.stackedBarChartEmptyLabel',
        defaultMessage: 'Please train your model to view prediction accuracy.'
    }
});

const stackedBarChartTheme = getScopedTheme('StackedBarChart');

/**
 * The stacked bar chart component
 *
 * @params props the properties of the stacked bar chart.
 */
export const StackedBarChart = <X extends Primitive>(props: StackedBarChartProps<X>) => {
    const { localize } = useLocalization();
    const {
        xData,
        yData,
        groups = [],
        colors = [stackedBarChartTheme.firstColor, stackedBarChartTheme.secondColor, stackedBarChartTheme.thirdColor],
        emptyChartLabel = localize(messages.stackedBarChartEmptyLabel),
        onRenderTooltip,
        barWidth = 20,
        dataToFocus
    } = props;

    const jsStackedBarChartClassName = 'js-stacked-bar-chart';
    const groupsColors = getGroupsColors(groups, colors);

    const configs: c3.ChartConfiguration = {
        data: {
            columns: transformStackedBarChartData(groups, yData),
            type: 'bar',
            groups: [groups],
            empty: {
                label: {
                    text: emptyChartLabel
                }
            },
            order: null,
            color: (_, d: DataSeries | DataPoint) => {
                if (d.id) {
                    return groupsColors[d.id];
                }
            }
        },

        tooltip: {
            contents: d => renderToString(onRenderTooltip(d[0].index))
        },

        bar: {
            width: xData.data.length >= MIN_WITHOUT_PADDING ? MAX_BARS / xData.data.length : barWidth
        },

        axis: {
            x: {
                label: {
                    text: xData.label,
                    position: 'outer-center'
                },
                tick: {
                    outer: false
                },
                padding:
                    xData.data.length < MIN_WITHOUT_PADDING
                        ? {
                              left: (MIN_WITHOUT_PADDING - xData.data.length) / 2,
                              right: (MIN_WITHOUT_PADDING - xData.data.length) / 2
                          }
                        : {}
            },
            y: {
                tick: {
                    count: 6,
                    format: (d: number) => d.toFixed()
                },
                padding: { top: 0, bottom: 0 }
            }
        }
    };

    return <BasicChart className={jsStackedBarChartClassName} {...configs} dataToFocus={dataToFocus} />;
};

/**
 * Generates an object that specify color for the groups.
 *
 * @param groups The data groups.
 * @param colors The groups colors.
 * @returns An object that has the key as the group name and the value is the color.
 */
const getGroupsColors = (groups: string[], colors: string[]): { [key: string]: string } =>
    groups.reduce((acc, gp, index) => ({ ...acc, [gp]: colors[index] }), {});
